/**
 * 财务中心 - 实时余额页
 *
 * 面向财务角色实时查看某服务商结算账户下全部记账子单元（含默认子单元）的余额，页面结构：
 * 1. 查询条件：服务商下拉（必选，未选前端拦截）+ 子单元下拉（选填，不选查全部含默认子单元，选中查仅返回该子单元行）；
 *    进页服务商下拉加载成功且非空时默认选第一项并自动查询全部子单元（下拉为空或加载失败不自动查）；
 *    服务商切换联动重载子单元下拉选项并清空已选，服务商与子单元选中均不自动查询，点查询按钮生效；
 *    查询与手动刷新均重新调银行（从首屏重拉），银行调用耗时长全程 loading
 * 2. 余额列表：列=服务商账号/名称/编号/生效日期/余额/状态；服务商账号取查询时所选服务商账号；
 *    余额与各列均为银行原值原样展示不做换算，空值显示空白；状态=银行处理中的行整行标灰提示，避免误当可用余额
 * 3. 滚动加载：银行单批返回一批余额与续传游标，首屏一批，滚动到表格底部自动带游标追加下一批直到取完；
 *    查询条件变化后缓冲区与游标全量重置从首屏重拉；取完后提示"已全部加载"；
 *    银行续传中断时提示结果可能不完整
 */
<template>
  <div class="finance-balance-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item>财务中心</el-breadcrumb-item>
      <el-breadcrumb-item>实时余额</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="list-card" shadow="never">
      <div slot="header" class="card-header">
        <span>子单元实时余额</span>
      </div>

      <el-form :inline="true" :model="queryForm" size="small" class="filter-form">
        <el-form-item label="服务商" required>
          <el-select
            v-model="queryForm.accountNumber"
            filterable
            clearable
            placeholder="请选择服务商"
            style="width: 280px"
            @change="handleProviderChange"
          >
            <el-option
              v-for="p in providerOptions"
              :key="p.accountNumber"
              :label="providerLabel(p)"
              :value="p.accountNumber"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="子单元">
          <el-select
            v-model="queryForm.dmanbr"
            filterable
            clearable
            placeholder="不选默认查全部"
            :loading="subunitLoading"
            style="width: 260px"
          >
            <el-option
              v-for="s in subunitOptions"
              :key="s.dmanbr"
              :label="subunitLabel(s)"
              :value="s.dmanbr"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="listLoading" @click="handleQuery">查询</el-button>
          <el-button
            type="primary"
            plain
            :disabled="!queryForm.accountNumber"
            :loading="listLoading"
            @click="handleRefresh"
          >刷新</el-button>
        </el-form-item>
      </el-form>

      <div v-if="queried" class="load-info">
        <span>已加载 {{ rows.length }} 条<template v-if="rows.length && !hasMore">，已全部加载</template></span>
        <span v-if="scrollLoading" class="scroll-loading-text">加载中...</span>
        <el-alert
          v-if="truncated"
          type="warning"
          :closable="false"
          show-icon
          class="load-alert"
          title="银行续传中断，已加载结果可能不完整，可调整条件重新查询"
        ></el-alert>
      </div>

      <el-table
        ref="listTable"
        :data="rows"
        v-loading="listLoading"
        border
        size="small"
        height="520"
        :row-class-name="balanceRowClass"
      >
        <el-table-column label="服务商账号" width="170">
          <template slot-scope="{ row }">{{ listAccountNumber }}</template>
        </el-table-column>
        <el-table-column prop="dmanam" label="子单元名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.dmanam || defaultName(row) }}</template>
        </el-table-column>
        <el-table-column prop="dmanbr" label="子单元编号" min-width="150"></el-table-column>
        <el-table-column label="生效日期" width="110">
          <template slot-scope="{ row }">{{ eftdatText(row.eftdat) }}</template>
        </el-table-column>
        <el-table-column prop="actbal" label="余额" min-width="140" align="right">
          <template slot-scope="{ row }">{{ actbalText(row) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">{{ stscodText(row.stscod) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { getFinanceProviderList, getFinanceSubunitList, getFinanceBalanceList } from '@/api/userMG';
import financeScrollList from './financeScrollList';

export default {
  name: 'FinanceBalance',
  mixins: [financeScrollList],
  data() {
    return {
      // 服务商下拉选项
      providerOptions: [],
      // 当前服务商下子单元下拉选项（含已关闭行，与交易明细/内部调配同源）
      subunitOptions: [],
      subunitLoading: false,
      // 查询条件
      queryForm: {
        accountNumber: '',
        dmanbr: ''
      },
      listLoading: false,
      // 本次列表对应的服务商账号（查询成功时固化，避免下拉改后表头账号与数据不一致）
      listAccountNumber: ''
    };
  },
  created() {
    this.loadProviders();
  },
  mounted() {
    this.bindTableScroll('listTable');
  },
  methods: {
    // 服务商下拉展示文案：账户名（账号）
    providerLabel(p) {
      return (p.accountName || '') + '（' + (p.accountNumber || '') + '）';
    },
    // 子单元下拉展示文案：名称（编号），默认子单元名称取不到时兜底展示（与交易明细同款）
    subunitLabel(s) {
      const name = s.dmanam || (s.defaultSubunit ? '默认子单元' : '');
      return name + '（' + s.dmanbr + '）';
    },
    // 加载服务商下拉（招商银行记账子单元服务商账户）
    async loadProviders() {
      try {
        const res = await getFinanceProviderList();
        if (res && res.code === 0) {
          this.providerOptions = res.data || [];
          this.initDefaultQuery();
        } else {
          this.$message.error((res && res.msg) || '服务商列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      }
    },
    // 进页默认查询：服务商下拉非空时默认选第一项并自动查询全部子单元（下拉为空或加载失败不自动查）
    initDefaultQuery() {
      if (!this.providerOptions.length) {
        return;
      }
      this.queryForm.accountNumber = this.providerOptions[0].accountNumber;
      this.loadSubunits();
      this.loadFirstBatch();
    },
    // 切换服务商后重置子单元单选并联动重载子单元下拉选项（不触发余额查询，点查询按钮生效）
    handleProviderChange() {
      this.queryForm.dmanbr = '';
      this.subunitOptions = [];
      if (this.queryForm.accountNumber) {
        this.loadSubunits();
      }
    },
    // 加载当前服务商下子单元下拉选项（与交易明细/内部调配同源接口；响应为统一契约，下拉取 records）
    async loadSubunits() {
      this.subunitLoading = true;
      try {
        const res = await getFinanceSubunitList({ accountNumber: this.queryForm.accountNumber });
        if (res && res.code === 0) {
          const data = res.data || {};
          this.subunitOptions = data.records || [];
        } else {
          this.$message.error((res && res.msg) || '子单元列表加载失败');
        }
      } catch (e) {
        // HTTP 400/500 由响应拦截器统一弹出后端 msg，此处仅兜底
      } finally {
        this.subunitLoading = false;
      }
    },
    // 查询前校验服务商必选，未选拦截不调银行；查询条件变化后从首屏重拉
    handleQuery() {
      if (!this.queryForm.accountNumber) {
        this.$message.warning('请先选择服务商');
        return;
      }
      this.loadFirstBatch();
    },
    // 手动刷新：按当前所选服务商重新调银行（从首屏重拉）
    handleRefresh() {
      if (!this.queryForm.accountNumber) {
        this.$message.warning('请先选择服务商');
        return;
      }
      this.loadFirstBatch();
    },
    // 当前查询条件参数（不含游标；续传游标由滚动加载逻辑自动并入）
    buildQueryParams() {
      const params = { accountNumber: this.queryForm.accountNumber };
      const dmanbr = (this.queryForm.dmanbr || '').trim();
      if (dmanbr) {
        params.dmanbr = dmanbr;
      }
      return params;
    },
    // 调列表接口拉一批（银行原值原样返回）；业务失败提示后返回 null，缓冲区保持不动
    async fetchBatch(params) {
      const res = await getFinanceBalanceList(params);
      if (res && res.code === 0) {
        return res.data || {};
      }
      this.$message.error((res && res.msg) || '余额查询失败');
      return null;
    },
    // 本批落库后固化服务商账号（追加与首屏同条件，仅首屏赋值即可）
    afterBatchLoaded(append) {
      if (!append) {
        this.listAccountNumber = this.queryForm.accountNumber;
      }
    },
    // 名称列兜底：编号全 0 的默认子单元银行可能不带名称
    defaultName(row) {
      return /^0+$/.test(row.dmanbr || '') ? '默认子单元' : '';
    },
    // 余额列：银行原值原样展示不做换算，空值显示空白
    actbalText(row) {
      const v = row.actbal;
      if (v === null || v === undefined || v === '') return '';
      return v;
    },
    // 生效日期列：银行原值 yyyyMMdd 转 yyyy-MM-dd，空值显示空白
    eftdatText(v) {
      if (!v || v.length !== 8) return '';
      return v.slice(0, 4) + '-' + v.slice(4, 6) + '-' + v.slice(6, 8);
    },
    // 状态列：A 生效，W 银行处理中，其余显示空白
    stscodText(v) {
      if (v === 'A') return '生效';
      if (v === 'W') return '银行处理中';
      return '';
    },
    // 银行处理中的行整行标灰
    balanceRowClass({ row }) {
      return row.stscod === 'W' ? 'bank-processing-row' : '';
    }
  }
};
</script>

<style scoped>
.page-breadcrumb {
  margin-bottom: 16px;
}
.filter-form {
  margin-bottom: 4px;
}
.load-info {
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}
.scroll-loading-text {
  margin-left: 12px;
  color: #909399;
}
.load-alert {
  margin-top: 6px;
  padding: 4px 12px;
}
.list-card >>> .el-table .bank-processing-row {
  color: #909399;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/balance.vue